"use strict";
/*!
 * Copyright 2018 Google Inc. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

var _classCallCheck = require("D:\\new_kisan\\kisan_web\\node_modules\\babel-preset-react-app\\node_modules\\@babel\\runtime/helpers/classCallCheck");

var _createClass = require("D:\\new_kisan\\kisan_web\\node_modules\\babel-preset-react-app\\node_modules\\@babel\\runtime/helpers/createClass");

Object.defineProperty(exports, "__esModule", {
  value: true
});

var is = require("is");

var timestamp_1 = require("./timestamp");

var field_value_1 = require("./field-value");

var validate_1 = require("./validate");

var path_1 = require("./path");

var convert_1 = require("./convert");

var geo_point_1 = require("./geo-point");
/**
 * Serializer that is used to convert between JavaScripts types and their
 * Firestore Protobuf representation.
 *
 * @private
 */


var Serializer =
/*#__PURE__*/
function () {
  function Serializer(firestore) {
    _classCallCheck(this, Serializer);

    // Instead of storing the `firestore` object, we store just a reference to
    // its `.doc()` method. This avoid a circular reference, which breaks
    // JSON.stringify().
    this.createReference = function (path) {
      return firestore.doc(path);
    };

    this.timestampsInSnapshots = !!firestore._settings.timestampsInSnapshots;
  }
  /**
   * Encodes a JavaScrip object into the Firestore 'Fields' representation.
   *
   * @private
   * @param obj The object to encode.
   * @returns The Firestore 'Fields' representation
   */


  _createClass(Serializer, [{
    key: "encodeFields",
    value: function encodeFields(obj) {
      var fields = {};

      for (var prop in obj) {
        if (obj.hasOwnProperty(prop)) {
          var val = this.encodeValue(obj[prop]);

          if (val) {
            fields[prop] = val;
          }
        }
      }

      return fields;
    }
    /**
     * Encodes a JavaScript value into the Firestore 'Value' representation.
     *
     * @private
     * @param val The object to encode
     * @returns The Firestore Proto or null if we are deleting a field.
     */

  }, {
    key: "encodeValue",
    value: function encodeValue(val) {
      if (val instanceof field_value_1.FieldTransform) {
        return null;
      }

      if (is.string(val)) {
        return {
          stringValue: val
        };
      }

      if (typeof val === 'boolean') {
        return {
          booleanValue: val
        };
      }

      if (typeof val === 'number' && is.integer(val)) {
        return {
          integerValue: val
        };
      } // Integers are handled above, the remaining numbers are treated as doubles


      if (is.number(val)) {
        return {
          doubleValue: val
        };
      }

      if (is.date(val)) {
        var timestamp = timestamp_1.Timestamp.fromDate(val);
        return {
          timestampValue: {
            seconds: timestamp.seconds,
            nanos: timestamp.nanoseconds
          }
        };
      }

      if (val === null) {
        return {
          nullValue: 'NULL_VALUE'
        };
      }

      if (val instanceof Buffer || val instanceof Uint8Array) {
        return {
          bytesValue: val
        };
      }

      if (typeof val === 'object' && 'toProto' in val && typeof val.toProto === 'function') {
        return val.toProto();
      }

      if (val instanceof Array) {
        var array = {
          arrayValue: {}
        };

        if (val.length > 0) {
          array.arrayValue.values = [];

          for (var i = 0; i < val.length; ++i) {
            var enc = this.encodeValue(val[i]);

            if (enc) {
              array.arrayValue.values.push(enc);
            }
          }
        }

        return array;
      }

      if (typeof val === 'object' && isPlainObject(val)) {
        var map = {
          mapValue: {}
        }; // If we encounter an empty object, we always need to send it to make sure
        // the server creates a map entry.

        if (!is.empty(val)) {
          map.mapValue.fields = this.encodeFields(val);

          if (is.empty(map.mapValue.fields)) {
            return null;
          }
        }

        return map;
      }

      throw validate_1.customObjectError(val);
    }
    /**
     * Decodes a single Firestore 'Value' Protobuf.
     *
     * @private
     * @param proto A Firestore 'Value' Protobuf.
     * @returns The converted JS type.
     */

  }, {
    key: "decodeValue",
    value: function decodeValue(proto) {
      var valueType = convert_1.detectValueType(proto);

      switch (valueType) {
        case 'stringValue':
          {
            return proto.stringValue;
          }

        case 'booleanValue':
          {
            return proto.booleanValue;
          }

        case 'integerValue':
          {
            return Number(proto.integerValue);
          }

        case 'doubleValue':
          {
            return Number(proto.doubleValue);
          }

        case 'timestampValue':
          {
            var timestamp = timestamp_1.Timestamp.fromProto(proto.timestampValue);
            return this.timestampsInSnapshots ? timestamp : timestamp.toDate();
          }

        case 'referenceValue':
          {
            var resourcePath = path_1.ResourcePath.fromSlashSeparatedString(proto.referenceValue);
            return this.createReference(resourcePath.relativeName);
          }

        case 'arrayValue':
          {
            var array = [];

            if (is.array(proto.arrayValue.values)) {
              var _iteratorNormalCompletion = true;
              var _didIteratorError = false;
              var _iteratorError = undefined;

              try {
                for (var _iterator = proto.arrayValue.values[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
                  var value = _step.value;
                  array.push(this.decodeValue(value));
                }
              } catch (err) {
                _didIteratorError = true;
                _iteratorError = err;
              } finally {
                try {
                  if (!_iteratorNormalCompletion && _iterator.return != null) {
                    _iterator.return();
                  }
                } finally {
                  if (_didIteratorError) {
                    throw _iteratorError;
                  }
                }
              }
            }

            return array;
          }

        case 'nullValue':
          {
            return null;
          }

        case 'mapValue':
          {
            var obj = {};
            var fields = proto.mapValue.fields;

            for (var prop in fields) {
              if (fields.hasOwnProperty(prop)) {
                obj[prop] = this.decodeValue(fields[prop]);
              }
            }

            return obj;
          }

        case 'geoPointValue':
          {
            return geo_point_1.GeoPoint.fromProto(proto.geoPointValue);
          }

        case 'bytesValue':
          {
            return proto.bytesValue;
          }

        default:
          {
            throw new Error('Cannot decode type from Firestore Value: ' + JSON.stringify(proto));
          }
      }
    }
  }]);

  return Serializer;
}();

exports.Serializer = Serializer;
/**
 * Verifies that 'obj' is a plain JavaScript object that can be encoded as a
 * 'Map' in Firestore.
 *
 * @private
 * @param input The argument to verify.
 * @returns 'true' if the input can be a treated as a plain object.
 */

function isPlainObject(input) {
  return typeof input === 'object' && input !== null && (Object.getPrototypeOf(input) === Object.prototype || Object.getPrototypeOf(input) === null);
}

exports.isPlainObject = isPlainObject;